/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { Injectable } from "@angular/core";
import {
  CustomComponent,
  CustomComponentFormData,
  UnifiedComponentItem,
} from "../interfaces/custom-component.interface";
import {
  DetailedSummaryComponent,
  DetailedSummaryConfig,
  UnifiedComponentOrderItem,
  CustomComponentContent,
} from "../interfaces/customer-portal-settings.interface";

/**
 * Service for managing custom components - API-based implementation
 *
 * This service converts between QMC internal format and API format,
 * and provides helper methods for manipulating custom component data.
 * Data is persisted through the CustomerPortalSettings API.
 */
@Injectable({
  providedIn: "root",
})
export class CustomComponentStorageService {
  // ============================================================================
  // CONVERSION METHODS: QMC Format <-> API Format
  // ============================================================================

  /**
   * Convert API CustomComponentContent to QMC CustomComponent format
   */
  apiToCustomComponent(apiComponent: CustomComponentContent): CustomComponent {
    return {
      id: apiComponent.Id,
      name: apiComponent.Name,
      htmlContent: apiComponent.HtmlContent,
      cssContent: apiComponent.CssContent,
      enabled: apiComponent.Enabled,
      order: apiComponent.Order,
      createdAt: apiComponent.CreatedAt || new Date().toISOString(),
      updatedAt: apiComponent.UpdatedAt || new Date().toISOString(),
    };
  }

  /**
   * Convert QMC CustomComponent to API CustomComponentContent format
   */
  customComponentToApi(component: CustomComponent): CustomComponentContent {
    return {
      Id: component.id,
      Name: component.name,
      HtmlContent: component.htmlContent,
      CssContent: component.cssContent,
      Enabled: component.enabled,
      Order: component.order,
      CreatedAt: component.createdAt,
      UpdatedAt: component.updatedAt,
    };
  }

  /**
   * Convert API UnifiedComponentOrderItem to QMC UnifiedComponentItem format
   */
  apiToUnifiedItem(
    apiItem: UnifiedComponentOrderItem,
    customComponents: CustomComponent[],
  ): UnifiedComponentItem {
    const customData = apiItem.IsCustom
      ? customComponents.find((c) => c.id === apiItem.Identifier)
      : undefined;

    return {
      identifier: apiItem.Identifier,
      label: apiItem.Label,
      order: apiItem.Order,
      enabled: apiItem.Enabled,
      isCustom: apiItem.IsCustom,
      customData,
    };
  }

  /**
   * Convert QMC UnifiedComponentItem to API UnifiedComponentOrderItem format
   */
  unifiedItemToApi(item: UnifiedComponentItem): UnifiedComponentOrderItem {
    return {
      Identifier: item.identifier,
      Label: item.label,
      Order: item.order,
      Enabled: item.enabled,
      IsCustom: item.isCustom,
    };
  }

  // ============================================================================
  // DATA EXTRACTION FROM API RESPONSE
  // ============================================================================

  /**
   * Extract custom components from DetailedSummaryConfig API response
   */
  getCustomComponentsFromConfig(
    config: DetailedSummaryConfig | null | undefined,
  ): CustomComponent[] {
    if (!config?.CustomComponents) {
      return [];
    }

    return config.CustomComponents.map((apiComp) =>
      this.apiToCustomComponent(apiComp),
    ).sort((a, b) => a.order - b.order);
  }

  /**
   * Extract unified order from DetailedSummaryConfig API response
   * Returns null if no unified order exists (backward compatibility)
   */
  getUnifiedOrderFromConfig(
    config: DetailedSummaryConfig | null | undefined,
    customComponents: CustomComponent[],
  ): UnifiedComponentItem[] | null {
    if (!config?.UnifiedOrder) {
      return null;
    }

    return config.UnifiedOrder.map((apiItem) =>
      this.apiToUnifiedItem(apiItem, customComponents),
    ).sort((a, b) => a.order - b.order);
  }

  // ============================================================================
  // DATA PREPARATION FOR API SAVE
  // ============================================================================

  /**
   * Prepare custom components for API save
   */
  prepareCustomComponentsForApi(
    components: CustomComponent[],
  ): CustomComponentContent[] {
    return components.map((comp) => this.customComponentToApi(comp));
  }

  /**
   * Prepare unified order for API save
   */
  prepareUnifiedOrderForApi(
    items: UnifiedComponentItem[],
  ): UnifiedComponentOrderItem[] {
    return items.map((item) => this.unifiedItemToApi(item));
  }

  /**
   * Update DetailedSummaryConfig with current custom components and unified order
   */
  updateConfigForSave(
    config: DetailedSummaryConfig,
    unifiedOrder: UnifiedComponentItem[],
    customComponents: CustomComponent[],
  ): DetailedSummaryConfig {
    return {
      ...config,
      UnifiedOrder: this.prepareUnifiedOrderForApi(unifiedOrder),
      CustomComponents: this.prepareCustomComponentsForApi(customComponents),
    };
  }

  // ============================================================================
  // COMPONENT MANIPULATION METHODS
  // ============================================================================

  /**
   * Create a new custom component
   */
  createComponent(formData: CustomComponentFormData): CustomComponent {
    const now = new Date().toISOString();
    return {
      id: `custom-${this.generateUUID()}`,
      name: formData.name,
      htmlContent: formData.htmlContent,
      cssContent: formData.cssContent,
      enabled: formData.enabled,
      order: formData.order,
      createdAt: now,
      updatedAt: now,
    };
  }

  /**
   * Update an existing custom component in the array
   * Returns updated array
   */
  updateComponentInArray(
    components: CustomComponent[],
    id: string,
    updates: Partial<CustomComponentFormData>,
  ): { components: CustomComponent[]; updated: CustomComponent | null } {
    const index = components.findIndex((c) => c.id === id);

    if (index === -1) {
      return { components, updated: null };
    }

    const updatedComponent: CustomComponent = {
      ...components[index],
      ...updates,
      updatedAt: new Date().toISOString(),
    };

    const newComponents = [...components];
    newComponents[index] = updatedComponent;

    return { components: newComponents, updated: updatedComponent };
  }

  /**
   * Delete a custom component from the array
   * Returns updated array
   */
  deleteComponentFromArray(
    components: CustomComponent[],
    id: string,
  ): CustomComponent[] {
    return components.filter((c) => c.id !== id);
  }

  /**
   * Get enabled custom components
   */
  getEnabledComponents(components: CustomComponent[]): CustomComponent[] {
    return components.filter((c) => c.enabled);
  }

  /**
   * Find a component by ID
   */
  findComponent(
    components: CustomComponent[],
    id: string,
  ): CustomComponent | null {
    return components.find((c) => c.id === id) || null;
  }

  // ============================================================================
  // UNIFIED ORDER METHODS
  // ============================================================================

  /**
   * Initialize unified order from standard and custom components
   * Creates a merged list with proper ordering
   */
  initializeUnifiedOrder(
    standardComponents: DetailedSummaryComponent[],
    customComponents: CustomComponent[],
    existingUnifiedOrder: UnifiedComponentItem[] | null,
  ): UnifiedComponentItem[] {
    // If we have existing unified order, sync it with current components
    if (existingUnifiedOrder && existingUnifiedOrder.length > 0) {
      return this.syncUnifiedOrder(
        existingUnifiedOrder,
        standardComponents,
        customComponents,
      );
    }

    // Build new unified order: standard components first, then custom
    const unifiedItems: UnifiedComponentItem[] = [];

    // Add standard components in their current order
    standardComponents.forEach((comp) => {
      unifiedItems.push({
        identifier: comp.Identifier,
        label: comp.Label,
        order: comp.Order,
        enabled: comp.Enabled,
        isCustom: false,
      });
    });

    // Add custom components after standard ones
    customComponents.forEach((comp) => {
      unifiedItems.push({
        identifier: comp.id,
        label: comp.name,
        order: comp.order,
        enabled: comp.enabled,
        isCustom: true,
        customData: comp,
      });
    });

    // Sort by order and re-normalize
    unifiedItems.sort((a, b) => a.order - b.order);
    unifiedItems.forEach((item, idx) => (item.order = idx));

    return unifiedItems;
  }

  /**
   * Sync existing unified order with current standard and custom components
   * Handles additions/removals of components
   */
  private syncUnifiedOrder(
    existingOrder: UnifiedComponentItem[],
    standardComponents: DetailedSummaryComponent[],
    customComponents: CustomComponent[],
  ): UnifiedComponentItem[] {
    const syncedItems: UnifiedComponentItem[] = [];
    const standardIds = new Set(standardComponents.map((c) => c.Identifier));
    const customIds = new Set(customComponents.map((c) => c.id));

    // Keep existing items that still exist
    existingOrder.forEach((item) => {
      if (item.isCustom) {
        if (customIds.has(item.identifier)) {
          // Update custom component data
          const customComp = customComponents.find(
            (c) => c.id === item.identifier,
          );
          if (customComp) {
            item.customData = customComp;
            item.label = customComp.name;
            item.enabled = customComp.enabled;
          }
          syncedItems.push(item);
        }
      } else {
        if (standardIds.has(item.identifier)) {
          // Update label from standard components
          const standardComp = standardComponents.find(
            (c) => c.Identifier === item.identifier,
          );
          if (standardComp) {
            item.label = standardComp.Label;
          }
          syncedItems.push(item);
        }
      }
    });

    // Add new standard components that aren't in the existing order
    const existingIds = new Set(existingOrder.map((i) => i.identifier));
    standardComponents.forEach((comp) => {
      if (!existingIds.has(comp.Identifier)) {
        syncedItems.push({
          identifier: comp.Identifier,
          label: comp.Label,
          order: comp.Order,
          enabled: comp.Enabled,
          isCustom: false,
        });
      }
    });

    // Add new custom components that aren't in the existing order
    customComponents.forEach((comp) => {
      if (!existingIds.has(comp.id)) {
        syncedItems.push({
          identifier: comp.id,
          label: comp.name,
          order: comp.order,
          enabled: comp.enabled,
          isCustom: true,
          customData: comp,
        });
      }
    });

    // Re-sort and normalize order
    syncedItems.sort((a, b) => a.order - b.order);
    syncedItems.forEach((item, idx) => (item.order = idx));

    return syncedItems;
  }

  /**
   * Add a new custom component to the unified order
   */
  addComponentToUnifiedOrder(
    component: CustomComponent,
    unifiedOrder: UnifiedComponentItem[],
  ): UnifiedComponentItem[] {
    const newItem: UnifiedComponentItem = {
      identifier: component.id,
      label: component.name,
      order: component.order,
      enabled: component.enabled,
      isCustom: true,
      customData: component,
    };

    // Insert at the specified order position
    const updatedOrder = [...unifiedOrder];

    // Find the right position
    let insertIndex = updatedOrder.findIndex(
      (item) => item.order >= component.order,
    );
    if (insertIndex === -1) {
      insertIndex = updatedOrder.length;
    }

    updatedOrder.splice(insertIndex, 0, newItem);

    // Re-normalize order values
    updatedOrder.forEach((item, idx) => (item.order = idx));

    return updatedOrder;
  }

  /**
   * Remove a custom component from the unified order
   */
  removeComponentFromUnifiedOrder(
    componentId: string,
    unifiedOrder: UnifiedComponentItem[],
  ): UnifiedComponentItem[] {
    const updatedOrder = unifiedOrder.filter(
      (item) => item.identifier !== componentId,
    );

    // Re-normalize order values
    updatedOrder.forEach((item, idx) => (item.order = idx));

    return updatedOrder;
  }

  /**
   * Update a component in the unified order
   */
  updateComponentInUnifiedOrder(
    componentId: string,
    updates: Partial<UnifiedComponentItem>,
    unifiedOrder: UnifiedComponentItem[],
  ): UnifiedComponentItem[] {
    return unifiedOrder.map((item) => {
      if (item.identifier === componentId) {
        return { ...item, ...updates };
      }
      return item;
    });
  }

  /**
   * Calculate the next available order value
   */
  getNextOrder(unifiedOrder: UnifiedComponentItem[]): number {
    if (unifiedOrder.length === 0) {
      return 0;
    }
    return Math.max(...unifiedOrder.map((c) => c.order)) + 1;
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  /**
   * Generate a UUID v4
   */
  generateUUID(): string {
    // Use crypto.randomUUID if available (modern browsers)
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID();
    }

    // Fallback for older browsers
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
      /[xy]/g,
      function (c) {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      },
    );
  }

  // ============================================================================
  // DEPRECATED: localStorage methods for backward compatibility during migration
  // These will be removed after full API migration is complete
  // ============================================================================

  private readonly STORAGE_KEY = "qpilot_custom_components";

  /**
   * @deprecated Use getCustomComponentsFromConfig instead
   * Clear localStorage data after successful API migration
   */
  clearLocalStorage(): void {
    localStorage.removeItem(this.STORAGE_KEY);
  }

  /**
   * @deprecated Check if there's legacy localStorage data that needs migration
   */
  hasLegacyLocalStorageData(siteId: number): boolean {
    const data = localStorage.getItem(this.STORAGE_KEY);
    if (!data) return false;

    try {
      const storage = JSON.parse(data);
      return storage.siteId === siteId && storage.components?.length > 0;
    } catch {
      return false;
    }
  }

  /**
   * @deprecated Get legacy data from localStorage for migration
   */
  getLegacyLocalStorageData(siteId: number): {
    components: CustomComponent[];
    unifiedOrder: UnifiedComponentItem[] | null;
  } | null {
    const data = localStorage.getItem(this.STORAGE_KEY);
    if (!data) return null;

    try {
      const storage = JSON.parse(data);
      if (storage.siteId !== siteId) return null;

      return {
        components: storage.components || [],
        unifiedOrder: storage.unifiedOrder || null,
      };
    } catch {
      return null;
    }
  }
}
